import React, { useMemo, useState } from 'react';
import { Box, Button, InputAdornment, Tab, Tabs, TextField, Typography } from '@mui/material';
import {
    SearchRounded as SearchIcon,
    CheckCircleRounded as SuccessIcon,
    HistoryRounded as AlreadyIcon,
    CancelRounded as FailIcon,
    CreditCardRounded as CardIcon,
    ChevronLeftRounded as ChevronIcon,
    TaskAltRounded as AllDoneIcon,
    UndoRounded as UndoIcon,
    ConfirmationNumberRounded as TokenIcon,
} from '@mui/icons-material';
import { toPersianNumber } from '../../utils/persianNumber';
import { M, SCAN_KIND, formatClock, initials, normalizeFa, toneColors } from './monitorTokens';
import { MealThumb, StatusPill } from './MonitorParts';
import { ScanEntry, UndoState } from './ScanResultPanel';

export interface DailyReservation {
    id: string;
    user: { id?: string; firstName: string; lastName: string; cardNumber: string; personnelCode: string; role?: string };
    meal: { title: string; imageUrl?: string | null };
    mealType: string;
    isDelivered: boolean;
    deliveredAt?: string | null;
    deliveryCode?: string | null;
}

export interface PendingPerson {
    key: string;
    user: DailyReservation['user'];
    meals: DailyReservation['meal'][];
    reservations: DailyReservation[];
}

const isGuest = (user: DailyReservation['user']) => user.role === 'guest';

// What to scan to deliver this person's meals. A card or personnel code delivers all of them in
// one scan; guests have neither, so each of their meals is delivered by its own token code.
export const deliveryIdentifiers = (person: PendingPerson): string[] => {
    const { user } = person;
    const code = user.personnelCode && user.personnelCode !== '-' ? user.personnelCode : '';
    const card = user.cardNumber && user.cardNumber !== '-' ? user.cardNumber : '';
    if (code || card) return [code || card];
    return person.reservations.map((r) => r.deliveryCode).filter((c): c is string => !!c);
};

const groupByPerson = (rows: DailyReservation[]) => {
    const map = new Map<string, PendingPerson>();
    for (const r of rows) {
        const key = r.user.id || `${r.user.personnelCode}|${r.user.firstName}|${r.user.lastName}`;
        const entry = map.get(key) ?? { key, user: r.user, meals: [], reservations: [] };
        entry.meals.push(r.meal);
        entry.reservations.push(r);
        map.set(key, entry);
    }
    return Array.from(map.values());
};

const RENDER_CAP = 120;

const historyIcon = (entry: ScanEntry) => {
    if (entry.kind === 'delivered') return <SuccessIcon />;
    if (entry.kind === 'already') return <AlreadyIcon />;
    if (entry.kind === 'unknownCard') return <CardIcon />;
    if (entry.kind === 'undone') return <UndoIcon />;
    return <FailIcon />;
};

const MealChips: React.FC<{ meals: { title: string; imageUrl?: string | null; quantity?: number }[]; size?: number; maxWidth?: string }> = ({ meals, size = 28, maxWidth = '45%' }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, maxWidth, overflow: 'hidden' }}>
        {meals.slice(0, 2).map((m, i) => (
            <Box key={`${m.title}-${i}`} sx={{ display: 'flex', alignItems: 'center', gap: 0.75, bgcolor: M.bg, borderRadius: '10px', pl: 1, pr: 0.5, py: 0.5, minWidth: 0 }}>
                <MealThumb imageUrl={m.imageUrl} title={m.title} size={size} />
                <Typography noWrap sx={{ fontFamily: M.font, fontSize: '0.85rem', color: M.textMuted }}>
                    {m.title}{m.quantity && m.quantity > 1 ? ` ×${toPersianNumber(m.quantity)}` : ''}
                </Typography>
            </Box>
        ))}
        {meals.length > 2 && (
            <Typography sx={{ fontFamily: M.font, fontSize: '0.85rem', color: M.textFaint }}>+{toPersianNumber(meals.length - 2)}</Typography>
        )}
    </Box>
);

const PersonAvatar: React.FC<{ user: DailyReservation['user'] }> = ({ user }) => (
    <Box sx={{ width: 40, height: 40, flexShrink: 0, borderRadius: '50%', bgcolor: isGuest(user) ? M.infoSoft : M.primarySoft, color: isGuest(user) ? M.infoText : M.primaryText, display: 'grid', placeItems: 'center', fontFamily: M.font, fontWeight: 800 }}>
        {isGuest(user) ? <TokenIcon fontSize="small" /> : initials(user.firstName, user.lastName)}
    </Box>
);

const personSubtitle = (person: PendingPerson) => {
    if (isGuest(person.user)) return `ژتون مهمان · ${toPersianNumber(person.reservations.length)} پرس`;
    const code = person.user.personnelCode;
    return code && code !== '-' ? `کد ${toPersianNumber(code)}` : 'بدون کد پرسنلی';
};

const undoButtonSx = {
    height: M.controlHeight, px: 1.5, borderRadius: M.radiusControl, flexShrink: 0,
    fontFamily: M.font, fontWeight: 700, color: M.dangerText, whiteSpace: 'nowrap',
    '&:hover': { bgcolor: M.dangerSoft },
};

type TabKey = 'pending' | 'delivered' | 'history';

const QueuePanel: React.FC<{
    pending: DailyReservation[];
    delivered: DailyReservation[];
    totalReservations: number;
    history: ScanEntry[];
    undoStateOf: (entry: ScanEntry) => UndoState;
    onSelect: (person: PendingPerson) => void;
    onUndoPerson: (person: PendingPerson) => void;
    onUndoEntry: (entry: ScanEntry) => void;
}> = ({ pending, delivered, totalReservations, history, undoStateOf, onSelect, onUndoPerson, onUndoEntry }) => {
    const [tab, setTab] = useState<TabKey>('pending');
    const [search, setSearch] = useState('');

    const pendingPeople = useMemo(() => groupByPerson(pending), [pending]);
    const deliveredPeople = useMemo(() => {
        const people = groupByPerson(delivered);
        const last = (p: PendingPerson) => Math.max(...p.reservations.map((r) => (r.deliveredAt ? new Date(r.deliveredAt).getTime() : 0)));
        return people.sort((a, b) => last(b) - last(a)); // most recent delivery first
    }, [delivered]);

    const matches = (p: PendingPerson) => {
        const q = normalizeFa(search);
        return !q
            || normalizeFa(`${p.user.firstName} ${p.user.lastName}`).includes(q)
            || normalizeFa(p.user.personnelCode || '').includes(q);
    };
    const list = (tab === 'delivered' ? deliveredPeople : pendingPeople).filter(matches);

    const searchBox = (
        <Box sx={{ p: 1.5, pb: 1 }}>
            <TextField
                fullWidth
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="جستجو در لیست: نام یا کد پرسنلی"
                inputProps={{ 'aria-label': 'جستجو در لیست' }}
                InputProps={{
                    startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: M.textFaint }} /></InputAdornment>,
                    sx: { height: M.controlHeight, borderRadius: M.radiusControl, fontFamily: M.font, color: M.text, bgcolor: M.bg, '& fieldset': { borderColor: M.border } },
                }}
            />
        </Box>
    );

    const listSx = { flex: 1, minHeight: 0, overflowY: 'auto', px: 1.5, pb: 1.5, display: 'flex', flexDirection: 'column', gap: 1, '& > *': { flexShrink: 0 } };

    const capNote = list.length > RENDER_CAP && (
        <Typography sx={{ fontFamily: M.font, color: M.textFaint, textAlign: 'center', py: 1 }}>
            {toPersianNumber(list.length - RENDER_CAP)} نفر دیگر — برای دیدن، جستجو کنید
        </Typography>
    );

    return (
        <Box sx={{ height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column', bgcolor: M.surface, border: `1px solid ${M.border}`, borderRadius: M.radiusCard, boxShadow: M.cardShadow, overflow: 'hidden' }}>
            <Tabs
                value={tab}
                onChange={(_, v) => setTab(v)}
                variant="fullWidth"
                sx={{
                    minHeight: 52, borderBottom: `1px solid ${M.border}`,
                    '& .MuiTab-root': { fontFamily: M.font, fontWeight: 700, fontSize: '0.95rem', minHeight: 52, color: M.textMuted, px: 1 },
                    '& .Mui-selected': { color: `${M.text} !important` },
                    '& .MuiTabs-indicator': { bgcolor: M.primary, height: 3 },
                }}
            >
                <Tab value="pending" label={`در انتظار (${toPersianNumber(pendingPeople.length)})`} />
                <Tab value="delivered" label={`تحویل‌شده (${toPersianNumber(deliveredPeople.length)})`} />
                <Tab value="history" label={`اسکن‌های اخیر (${toPersianNumber(history.length)})`} />
            </Tabs>

            {tab === 'pending' && (
                <>
                    {searchBox}
                    <Box sx={listSx}>
                        {list.slice(0, RENDER_CAP).map((p) => (
                            <Box
                                key={p.key}
                                component="button"
                                type="button"
                                onClick={() => onSelect(p)}
                                sx={{
                                    all: 'unset', boxSizing: 'border-box', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 1.5,
                                    p: 1.25, minHeight: 64, borderRadius: M.radiusControl, bgcolor: M.surfaceRaised, border: '1px solid transparent',
                                    transition: 'background-color 150ms, border-color 150ms',
                                    '&:hover': { bgcolor: M.surfaceHover, borderColor: M.borderStrong },
                                    '&:focus-visible': { outline: `3px solid ${M.primary}`, outlineOffset: 2 },
                                }}
                            >
                                <PersonAvatar user={p.user} />
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Typography noWrap sx={{ fontFamily: M.font, fontWeight: 700, color: M.text }}>
                                        {p.user.firstName || p.user.lastName ? `${p.user.firstName} ${p.user.lastName}` : 'نامشخص'}
                                    </Typography>
                                    <Typography noWrap sx={{ fontFamily: M.font, fontSize: '0.85rem', color: M.textFaint }}>{personSubtitle(p)}</Typography>
                                </Box>
                                <MealChips meals={p.meals} />
                                <ChevronIcon sx={{ color: M.textFaint }} />
                            </Box>
                        ))}
                        {capNote}
                        {list.length === 0 && (
                            <Box sx={{ flex: 1, display: 'grid', placeItems: 'center', textAlign: 'center', py: 4 }}>
                                {search ? (
                                    <Typography sx={{ fontFamily: M.font, color: M.textMuted }}>کسی در لیست انتظار با «{search}» نیست</Typography>
                                ) : totalReservations > 0 ? (
                                    <Box>
                                        <AllDoneIcon sx={{ fontSize: 56, color: M.success }} />
                                        <Typography sx={{ fontFamily: M.font, color: M.text, fontWeight: 700, mt: 1 }}>همه‌ی رزروهای این وعده تحویل شده‌اند</Typography>
                                    </Box>
                                ) : (
                                    <Typography sx={{ fontFamily: M.font, color: M.textMuted }}>برای این وعده رزروی ثبت نشده است</Typography>
                                )}
                            </Box>
                        )}
                    </Box>
                </>
            )}

            {tab === 'delivered' && (
                <>
                    {searchBox}
                    <Box sx={listSx}>
                        {list.slice(0, RENDER_CAP).map((p) => {
                            const lastAt = p.reservations
                                .map((r) => r.deliveredAt)
                                .filter((d): d is string => !!d)
                                .sort()
                                .pop();
                            return (
                                <Box key={p.key} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 1.25, minHeight: 64, borderRadius: M.radiusControl, bgcolor: M.surfaceRaised }}>
                                    <PersonAvatar user={p.user} />
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Typography noWrap sx={{ fontFamily: M.font, fontWeight: 700, color: M.text }}>
                                            {p.user.firstName} {p.user.lastName}
                                        </Typography>
                                        <Typography noWrap sx={{ fontFamily: M.font, fontSize: '0.85rem', color: M.successText }}>
                                            {lastAt ? `تحویل ساعت ${formatClock(lastAt)}` : 'تحویل شده'} · {personSubtitle(p)}
                                        </Typography>
                                    </Box>
                                    <MealChips meals={p.meals} maxWidth="35%" />
                                    <Button onClick={() => onUndoPerson(p)} startIcon={<UndoIcon />} sx={undoButtonSx} aria-label={`لغو تحویل ${p.user.firstName} ${p.user.lastName}`}>
                                        لغو تحویل
                                    </Button>
                                </Box>
                            );
                        })}
                        {capNote}
                        {list.length === 0 && (
                            <Typography sx={{ fontFamily: M.font, color: M.textMuted, textAlign: 'center', mt: 4 }}>
                                {search ? `کسی در تحویل‌شده‌ها با «${search}» نیست` : 'هنوز غذایی در این وعده تحویل نشده است'}
                            </Typography>
                        )}
                    </Box>
                </>
            )}

            {tab === 'history' && (
                <Box sx={{ ...listSx, pt: 1.5 }}>
                    {history.map((h) => {
                        const c = toneColors(SCAN_KIND[h.kind].tone);
                        const who = h.isGroup ? `گروه ${h.groupTitle}` : h.user ? `${h.user.firstName} ${h.user.lastName}` : h.identifier ? toPersianNumber(h.identifier) : 'نامشخص';
                        const undoState = undoStateOf(h);
                        const undone = undoState === 'undone';
                        const canUndo = undoState === 'available';
                        return (
                            <Box key={h.id} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 1.25, minHeight: 64, borderRadius: M.radiusControl, bgcolor: M.surfaceRaised, borderInlineStart: `4px solid ${c.main}`, opacity: undone ? 0.6 : 1 }}>
                                <Box sx={{ color: c.main, display: 'grid', placeItems: 'center' }}>{historyIcon(h)}</Box>
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Typography noWrap sx={{ fontFamily: M.font, fontWeight: 700, color: M.text }}>{who}</Typography>
                                    <Typography noWrap sx={{ fontFamily: M.font, fontSize: '0.85rem', color: c.text }}>
                                        {SCAN_KIND[h.kind].title} · {formatClock(h.at, true)}
                                    </Typography>
                                </Box>
                                {h.meals && h.meals.length > 0 && <MealChips meals={h.meals} size={24} maxWidth="35%" />}
                                {undone && <StatusPill tone="info" icon={<UndoIcon />}>لغو شد</StatusPill>}
                                {canUndo && (
                                    <Button onClick={() => onUndoEntry(h)} startIcon={<UndoIcon />} sx={undoButtonSx}>
                                        لغو
                                    </Button>
                                )}
                            </Box>
                        );
                    })}
                    {history.length === 0 && (
                        <Typography sx={{ fontFamily: M.font, color: M.textMuted, textAlign: 'center', mt: 4 }}>هنوز کارتی اسکن نشده است</Typography>
                    )}
                </Box>
            )}
        </Box>
    );
};

export default QueuePanel;
